import React from 'react';
import styled, { css } from 'styled-components';

import CustomBreadCrumbs from '@/components/atom/CustomBreadCrumbs';
import Divider from '@/components/atom/Divider';
import FixedButton from '@/components/atom/buttons/FixedButton';
import LinkTag from '@/components/common/LinkTag';
import ListMagazineLine from '@/components/common/communication/magazine/ListMagazineLine';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import RelatedQuestionItem from '@/components/templates/mobile/communication/Qna/QnaDetailMobileTemplate/QnaRelatedQuestion/RelatedQuestionItem';
import NonBenefitCategoryInformation from '@/components/templates/nonBenefit/home/components/NonBenefitCategoryInformation';
import NonBenefitSearchDoctorItem from '@/components/templates/nonBenefit/home/components/NonBenefitSearchDoctorItem';
import NonBenefitSearchDoctorList from '@/components/templates/nonBenefit/home/components/NonBenefitSearchDoctorList';
import NonBenefitTotalSummaryTable from '@/components/templates/nonBenefit/home/components/NonBenefitTotalSummaryTable';
import RelatedKeywordContentList from '@/components/templates/nonBenefit/home/components/RelatedKeywordContentList';
import SearchAddressFilterModal from '@/components/templates/nonBenefit/home/components/SearchAddressFilterModal';
import SearchBar from '@/components/templates/nonBenefit/home/components/SearchBar';
import useNonBenefitHomeViewModel from '@/hooks/nonBenefit/useNonBenefitHomeViewModel';
import theme from '@/styles/theme';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { formatPriceComma } from '@/utils/format';

interface Props extends CustomPageProps {
  viewModel: ReturnType<typeof useNonBenefitHomeViewModel>;
}

const NonBenefitHomeTemplate = ({ client, viewModel }: Props) => {
  return (
    <TemplateLayout.Wrapper>
      <CustomBreadCrumbs.Wrapper>
        <CustomBreadCrumbs visible={!client.isMobile} breadCrumbs={viewModel.breadCrumbs} />
      </CustomBreadCrumbs.Wrapper>
      <TemplateLayout.Content>
        <SearchFilterSection>
          <TitleWrapper>
            <NonBenefitTitle>{`${viewModel.selectedAddressTitle} ${viewModel.selectedMedicine.treatmentProductMedicineName} 최저가 가격 ${viewModel.selectedMedicine.medicineName} ${viewModel.selectedMedicine.medicineDescription} 2025`}</NonBenefitTitle>
            <NonBenefitDescription>{`닥터나우에서 ${viewModel.selectedAddressTitle} ${
              viewModel.selectedMedicine.treatmentProductMedicineName
            } 처방 성지 병원의 진료 가격을 비교한 결과,\n 최저가 ${
              viewModel.selectedMedicine.treatment.name
            } 진료비는 ${formatPriceComma(viewModel.selectedRegionSummary.minPrice)}원이에요.`}</NonBenefitDescription>
          </TitleWrapper>
          <SearchBarListWrapper>
            <SearchBar title={`${viewModel.selectedAddressTitle}`} onClick={viewModel.onOpenSearchAddressFilterModal} />
            <SearchBar
              title={`${viewModel.selectedMedicine.medicineName}`}
              onClick={viewModel.onOpenMedicineFilterModal}
            />
            <SearchBar
              title={`${viewModel.selectedMedicineUnit.unitDisplayName}`}
              onClick={viewModel.onOpenMedicineUnitFilterModal}
            />
          </SearchBarListWrapper>
        </SearchFilterSection>
        <Divider height={'16px'} color={theme.color.gray30} />
        <SearchSummarySection>
          <LinkTag
            href={`${ROUTES.NON_BENEFIT.MAP.path}?diagnosisTypeId=${viewModel.selectedMedicine.treatment?.id}&treatmentMedicineId=${viewModel.selectedMedicine.id}`}
          >
            <MapBannerWrapper>
              <MapBannerText>근처 최저가 약국을 찾고 싶다면?</MapBannerText>
              <MapBannerButton>
                <MapBannerButtonLabel>
                  {viewModel.selectedMedicine.treatmentProductMedicineName} 최저가 약국 찾기{' '}
                </MapBannerButtonLabel>
              </MapBannerButton>
            </MapBannerWrapper>
          </LinkTag>
          <SearchSummaryWrapper>
            <SearchSummaryInfoWrapper>
              <SearchSummaryTitle>
                {`${viewModel.selectedAddressTitle} ${viewModel.selectedMedicine.treatmentProductMedicineName} 처방 \n 최저가 진료 가격은\n ${viewModel.priceDisplayGapText}`}
              </SearchSummaryTitle>
              <SearchSummaryDescription>{`닥터나우에서 조회된 ${viewModel.selectedAddressTitle} ${viewModel.selectedMedicine.treatmentProductMedicineName} 처방\n성지 병원의 ${viewModel.selectedMedicineUnit.unitDisplayName} 진료 가격이에요.`}</SearchSummaryDescription>
            </SearchSummaryInfoWrapper>
            <SearchSummaryPricesWrapper>
              <SummaryPriceWrapper>
                <SummaryPriceMinTitle>최저가</SummaryPriceMinTitle>
                <SummaryPriceMin>{formatPriceComma(viewModel.selectedRegionSummary.minPrice)}원</SummaryPriceMin>
                <SummaryExcludeMedicine>약 가격 미포함</SummaryExcludeMedicine>
              </SummaryPriceWrapper>
              <SummaryPriceWrapper>
                <SummaryPriceTitle>평균가</SummaryPriceTitle>
                <SummaryPrice>{formatPriceComma(viewModel.selectedRegionSummary.averagePrice)}원</SummaryPrice>
                <SummaryExcludeMedicine>약 가격 미포함</SummaryExcludeMedicine>
              </SummaryPriceWrapper>
            </SearchSummaryPricesWrapper>
          </SearchSummaryWrapper>
        </SearchSummarySection>
        <Divider height={'16px'} color={theme.color.gray30} />
        <SearchDoctorSection>
          <SearchDoctorHeader>
            <SearchDoctorTitleWrapper>
              <SearchDoctorTitle>{`${viewModel.selectedAddressTitle} 성지 병원\n${viewModel.selectedMedicine.treatmentProductMedicineName} 처방 비대면 진료 가격 정보`}</SearchDoctorTitle>
              <SearchDoctorDescription>{`${viewModel.selectedAddressTitle} ${viewModel.selectedMedicine.treatmentProductMedicineName} 성지 병원의 ${viewModel.selectedMedicineUnit.unitDisplayName} 비대면 진료 가격이에요.\n닥터나우에서는 ${viewModel.selectedMedicine.treatmentProductMedicineName}을 전화로 편하게 받을 수 있어요.`}</SearchDoctorDescription>
            </SearchDoctorTitleWrapper>
          </SearchDoctorHeader>
          <NonBenefitSearchDoctorList
            list={viewModel.nonBenefitDoctors}
            ListEmptyComponent={
              <NonBenefitSearchDoctorItem.Empty
                hospitalAddress={viewModel.selectedFullAddressTitle}
                cheapestPrice={viewModel.selectedRegionSummary.minPrice}
                profileImageUrl={'https://file.doctornow.co.kr/doctors/default_profile.png'}
                onClick={viewModel.onOpenDiagnosisAppInstallBottomSheet}
              />
            }
            renderItem={(doctor, lastItem) => (
              <NonBenefitSearchDoctorItem
                key={`doctor-${doctor.doctorId}`}
                doctor={doctor}
                cheapestPrice={viewModel.selectedRegionSummary.minPrice}
                treatmentMedicineUnitId={viewModel.selectedMedicineUnit.id}
                lastItem={lastItem}
                onClick={() => viewModel.onClickDoctor(doctor, viewModel.selectedMedicine)}
              />
            )}
          />
          <SearchDoctorListButtonWrapper>
            <SearchDoctorListButton onClick={viewModel.onOpenDiagnosisAppInstallBottomSheet}>
              {`${viewModel.selectedAddressTitle} ${viewModel.selectedMedicine.treatmentProductMedicineName} 성지 병원 보러가기`}
            </SearchDoctorListButton>
          </SearchDoctorListButtonWrapper>
        </SearchDoctorSection>
        <Divider height={'16px'} color={theme.color.gray30} />
        <SearchTotalSummarySection>
          <SearchTotalSummaryHeader>
            <SearchTotalSummaryTitleWrapper>
              <SearchTotalSummaryTitle>{`${viewModel.selectedMedicine.treatmentProductMedicineName} 처방 성지 진료 가격 비교 (${viewModel.selectedMedicine.medicineProductName})`}</SearchTotalSummaryTitle>
              <SearchTotalSummaryDescriptionWrapper>
                <SearchTotalSummaryDescription>{`${viewModel.selectedAddressTitle} ${viewModel.selectedMedicine.treatmentProductMedicineName} 처방 성지 병원의 가격 비교예요.`}</SearchTotalSummaryDescription>
                <SearchTotalSummaryDescription>{`닥터나우 앱에서 ${viewModel.selectedAddressTitle}과(와) 전국의 ${viewModel.selectedMedicine.treatmentProductMedicineName} 처방 가격을 비교하고 비대면 진료 받아보세요.`}</SearchTotalSummaryDescription>
              </SearchTotalSummaryDescriptionWrapper>
            </SearchTotalSummaryTitleWrapper>
          </SearchTotalSummaryHeader>
          <SearchTotalSummarySubTitle>
            {`${viewModel.selectedMedicine.treatmentProductMedicineName} 처방 비대면 진료 가격`}
          </SearchTotalSummarySubTitle>
          <NonBenefitTotalSummaryTable
            nonBenefitPriceTotalSummary={viewModel.nonBenefitPriceTotalSummary}
            selectedAddressTitle={viewModel.selectedAddressTitle}
            selectedMedicine={viewModel.selectedMedicine}
          />
          <SearchTotalSummaryButtonWrapper>
            <SearchTotalSummaryButton
              onClick={viewModel.onOpenDiagnosisAppInstallBottomSheet}
            >{`${viewModel.selectedMedicine.treatmentProductMedicineName} 처방 최저가를 확인하고 싶다면?`}</SearchTotalSummaryButton>
          </SearchTotalSummaryButtonWrapper>
        </SearchTotalSummarySection>
        <Divider height={'16px'} color={theme.color.gray30} />
        <MagazineSection>
          <RelatedKeywordContentList
            list={viewModel.faqs}
            title={`${viewModel.selectedMedicine.treatmentProductMedicineName} 처방 자주 묻는 질문`}
            renderItem={(faq, isLast) => {
              return (
                <LinkTag
                  key={`faq-${faq.magazinePid}`}
                  href={`${ROUTES.FAQ.DETAIL.path}/${faq.magazinePid}`}
                  onClickNavigateToScreen={() => viewModel.onClickRelatedFaq(faq.magazinePid)}
                >
                  <ListMagazineLine
                    magazine={{
                      title: faq.title,
                      mobileTitle: faq.title,
                      image: '',
                      date: faq.createdDate,
                      magazinePid: faq.magazinePid,
                      description: faq.description,
                      mobileDescription: faq.mobileDescription,
                    }}
                    isLast={isLast}
                  />
                </LinkTag>
              );
            }}
          />
        </MagazineSection>
        <Divider height={'16px'} color={theme.color.gray30} />
        <MagazineSection>
          <RelatedKeywordContentList
            list={viewModel.magazines}
            title={`${viewModel.selectedMedicine.treatmentProductMedicineName} 처방 건강 매거진`}
            renderItem={(magazine, isLast) => {
              return (
                <LinkTag
                  key={`magazine-${magazine.magazinePid}`}
                  href={`${ROUTES.MAGAZINE.DETAIL.path}/${magazine.magazinePid}`}
                  onClickNavigateToScreen={() => viewModel.onClickRelatedMagazine(magazine.magazinePid)}
                >
                  <ListMagazineLine
                    magazine={{
                      title: magazine.title,
                      mobileTitle: magazine.title,
                      image: magazine.titleImageUrl,
                      date: magazine.createdDate,
                      magazinePid: magazine.magazinePid,
                      description: magazine.description,
                      mobileDescription: magazine.mobileDescription,
                    }}
                    isLast={isLast}
                  />
                </LinkTag>
              );
            }}
          />
        </MagazineSection>
        <Divider height={'16px'} color={theme.color.gray30} />
        <MagazineSection>
          <RelatedKeywordContentList
            list={viewModel.qnas}
            title={`${viewModel.selectedMedicine.treatmentProductMedicineName} 처방 의료진 상담`}
            renderItem={(qna, isLast) => {
              return (
                <LinkTag
                  key={`content_related_qna_${qna.question.questionPid}`}
                  href={`${ROUTES.QNA.DETAIL.path}/${qna.question.questionPid}`}
                  onClickNavigateToScreen={() => {
                    viewModel.onClickRelatedQuestion(qna.question.questionPid);
                  }}
                >
                  <RelatedQuestionItem
                    onClickRelatedQna={() => {
                      viewModel.onClickRelatedQuestion(qna.question.questionPid);
                    }}
                    questionCard={qna}
                    isLast={isLast}
                  />
                </LinkTag>
              );
            }}
          />
          {viewModel.isEnableQnaPagination && (
            <QnaMoreButtonWrapper>
              <QnaMoreButton
                onClick={viewModel.onClickNextQna}
              >{`${viewModel.selectedMedicine.treatmentProductMedicineName} 처방 의료진 상담 더 보기`}</QnaMoreButton>
            </QnaMoreButtonWrapper>
          )}
        </MagazineSection>
        <Divider height={'16px'} color={theme.color.gray30} />
        <MagazineSection>
          <NonBenefitCategoryInformation selectedMedicine={viewModel.selectedMedicine} />
        </MagazineSection>
        <FixedButton>
          <FixedButtonWrapper>
            <SearchSummaryNotice>{`${viewModel.selectedMedicine.treatment.name} 비대면 진료 가능해요!`}</SearchSummaryNotice>
            <FixedButton.Button
              text={`${viewModel.selectedMedicine.treatmentProductMedicineName} 처방받기`}
              onClick={viewModel.onOpenDiagnosisAppInstallBottomSheet}
            />
          </FixedButtonWrapper>
        </FixedButton>
      </TemplateLayout.Content>
      {/* 각종 파라미터가 매번 바로 업데이트되어야하는데 showModal 사용시 업데이트가 안되서 기존 방식으로 작업함 */}
      {viewModel.isAddressFilterModalOpen && (
        <SearchAddressFilterModal {...viewModel.getSearchAddressFilterModalProps()} />
      )}
    </TemplateLayout.Wrapper>
  );
};

export default NonBenefitHomeTemplate;

const BreadCrumbsWrapper = styled.div`
  padding: ${({ theme }) => (theme.client.isMobile ? '0' : '24px 24px 16px')};
`;

const TitleWrapper = styled.div`
  ${({ theme }) => css`
    padding: ${theme.client.isMobile ? 0 : 2}px 24px;
  `}
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 32px;
  justify-content: center;
`;

const NonBenefitTitle = styled.h1`
  ${({ theme }) => css`
    ${theme.typography.UI22SB}
    color: ${theme.color.gray900};
  `}
`;

const NonBenefitDescription = styled.p`
  ${({ theme }) => css`
    ${theme.typography.UI15R};
    color: ${theme.color.gray500};
    white-space: pre-line;
  `}
`;

const SearchBarListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 24px;
  gap: 16px;
  margin-bottom: 20px;
`;

const SearchButton = styled.button`
  ${({ theme }) => css`
    ${theme.typography.UI17SB}
    color: ${theme.color.white};
    background-color: ${theme.color.gray700};
  `}
  border: none;
  border-radius: 12px;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 56px;
  width: 100%;
`;

const SearchLargeButton = styled(SearchButton)`
  ${({ theme }) => css`
    ${theme.typography.UI16SB}
  `}
  height: 48px;
  border-radius: 12px;
`;

const SearchFilterSection = styled.section`
  ${({ theme }) => css`
    border-bottom: 1px solid ${theme.color.gray50};
  `}
`;

const SearchSummarySection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 32px;

  margin-top: 40px;
  ${({ theme }) => css`
    border-bottom: 1px solid ${theme.color.gray50};
  `}
`;

const SearchDoctorSection = styled.section`
  ${({ theme }) => css`
    border-bottom: 1px solid ${theme.color.gray50};
  `}
`;

const SearchTotalSummarySection = styled.section`
  ${({ theme }) => css`
    border-bottom: 1px solid ${theme.color.gray50};
  `}
`;

const SearchSummaryWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: ${theme.client.isMobile ? 'column' : 'row'};
    align-items: ${theme.client.isMobile ? 'flex-start' : 'center'};
    justify-content: ${theme.client.isMobile ? 'flex-start' : 'space-between'};
  `}
  padding: 0 24px;
  margin-bottom: 40px;
  gap: 52px;
`;

const SearchSummaryInfoWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SearchSummaryTitle = styled.h2`
  ${({ theme }) => css`
    ${theme.typography.UI22SB}
    color: ${theme.color.gray700};
    white-space: pre-line;
  `}
`;

const SearchSummaryDescription = styled.p`
  ${({ theme }) => css`
    ${theme.typography.UI15R}
    color: ${theme.color.gray500};
    white-space: pre-line;
  `}
`;

const SearchSummaryPricesWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 80px;
`;

const SummaryPriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SummaryPriceTitle = styled.p`
  ${({ theme }) => css`
    ${theme.typography.UI17SB}
    color: ${theme.color.gray600};
    margin-bottom: 8px;
  `}
`;

const SummaryPriceMinTitle = styled(SummaryPriceTitle)`
  color: ${({ theme }) => theme.color.primary600};
`;

const SummaryPrice = styled.p`
  ${({ theme }) => css`
    ${theme.typography.UI26SB}
    color: ${theme.color.gray800};
    margin-bottom: 4px;
  `}
`;

const SummaryPriceMin = styled(SummaryPrice)`
  color: ${({ theme }) => theme.color.primary700};
`;

const SummaryExcludeMedicine = styled.p`
  ${({ theme }) => css`
    ${theme.typography.UI14R}
    color: ${theme.color.gray300};
  `}
`;

const SearchSummaryNotice = styled.p`
  ${({ theme }) => css`
    text-align: center;
    ${theme.typography.UI16M}
    color: ${theme.color.gray700};
    margin-bottom: 12px;
    white-space: ${theme.client.isMobile ? 'pre-line' : 'normal'};
  `}
`;

const FixedButtonWrapper = styled.div`
  width: 100%;
`;

const SearchDoctorHeader = styled.div`
  padding: 16px 0;
`;

const SearchDoctorTitleWrapper = styled.div`
  padding: 16px 24px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SearchDoctorTitle = styled(SearchSummaryTitle)``;
const SearchDoctorDescription = styled(SearchSummaryDescription)`
  white-space: pre-line;
`;

const SearchDoctorListButtonWrapper = styled.div`
  padding: 0 24px;
  margin-bottom: 42px;
`;

const SearchDoctorListButton = styled(SearchLargeButton)`
  ${({ theme }) => css`
    color: ${theme.color.gray600};
    background-color: ${theme.color.white};
    border: 1px solid ${theme.color.gray100};
  `}
`;

const SearchTotalSummaryHeader = styled(SearchDoctorHeader)``;

const SearchTotalSummaryTitleWrapper = styled(SearchDoctorTitleWrapper)``;
const SearchTotalSummaryTitle = styled(SearchDoctorTitle)``;

const SearchTotalSummaryDescriptionWrapper = styled.div``;
const SearchTotalSummaryDescription = styled(SearchDoctorDescription)``;

const SearchTotalSummarySubTitle = styled.h3`
  padding: 10px 24px;
  ${({ theme }) => css`
    ${theme.typography.UI17SB}
    color: ${theme.color.gray600};
  `}
`;

const SearchTotalSummaryButtonWrapper = styled.div`
  margin-top: 20px;
  padding: 0 24px;
  margin-bottom: 40px;
`;

const SearchTotalSummaryButton = styled(SearchLargeButton)`
  ${({ theme }) => css`
    color: ${theme.color.primary800};
    background-color: ${theme.color.primary100};
  `}
`;

const MagazineSection = styled(SearchDoctorSection)`
  padding-bottom: 40px;
`;

const QnaMoreButtonWrapper = styled.div`
  padding: 35px 24px 0;
`;

const QnaMoreButton = styled(SearchDoctorListButton)``;

const MapBannerWrapper = styled.div`
  display: flex;
  height: 189px;
  padding: 59px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin: 0 24px;

  border-radius: 12px;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.5) 100%),
    url('https://file.doctornow.co.kr/non-benefit/non_benefit_map_thumbnail.png') lightgray 0 -0.013px / 117.448% 380.437%
      no-repeat;
`;

const MapBannerText = styled.div`
  color: ${({ theme }) => theme.color.white};
  text-align: center;

  ${({ theme }) => theme.typography.UI20SB}
`;

const MapBannerButton = styled.div`
  display: flex;
  height: 40px;
  padding: 0px 16px;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;

  border-radius: 8px;
  background: ${({ theme }) => theme.color.primary600};
`;

const MapBannerButtonLabel = styled.div`
  color: ${theme.color.white};
  ${({ theme }) => theme.typography.UI14SB}
`;
